import { format, parseISO } from "date-fns";

import type {
  AdminUserListItem,
  AdminUserNotice,
  NoticeFilter,
  NoticeType,
} from "./userApi";

export const NOTICE_TYPE_LABELS: Record<NoticeType, string> = {
  arbitration_opt_out: "Arbitration opt-out",
  tos_change_opt_out: "TOS change opt-out",
  privacy_request: "Privacy request",
  notice_of_dispute: "Notice of dispute",
  other: "Other",
};

/** Label for a notice type the API returned. Deploys go API-first, so the
 *  API can add a type before this UI knows it; show the raw type rather than
 *  "undefined". */
export const noticeTypeLabel = (type: string): string =>
  NOTICE_TYPE_LABELS[type as NoticeType] ?? type;

/** `YYYY-MM-DD` -> "Sep 20, 2026". parseISO reads a date-only string as
 *  LOCAL midnight, so the day never shifts (unlike `new Date("2026-09-20")`,
 *  which is UTC midnight and renders as the 19th anywhere west of UTC). */
export const formatReceivedOn = (receivedOn: string): string =>
  format(parseISO(receivedOn), "PP");

/** Today in the admin's own time zone, as `YYYY-MM-DD` (date input value/max). */
export const todayIsoDate = (now: Date = new Date()): string =>
  format(now, "yyyy-MM-dd");

/** The URL if it is an absolute http(s) link, else null. Notes render their
 *  URL as a link, so anything else (javascript:, data:) is shown as text. The
 *  API and a DB CHECK already refuse such URLs; this is the last line. */
export const safeHttpUrl = (url: string | null | undefined): string | null => {
  // The literal prefix, as omg-api and the DB CHECK require: the URL parser
  // would otherwise "repair" `https:example.com` into a valid URL.
  if (!url || !/^https?:\/\//i.test(url)) return null;
  try {
    const { protocol } = new URL(url);
    return protocol === "https:" || protocol === "http:" ? url : null;
  } catch {
    return null;
  }
};

const matchesFilter = (
  notices: readonly AdminUserNotice[],
  filter: NoticeFilter,
): boolean =>
  filter === "any"
    ? notices.length > 0
    : notices.some((notice) => notice.type === filter);

/** True when the API returned users that do NOT carry the requested notice,
 *  which means it ignored the `noticeType` filter rather than applying it.
 *
 *  Same failure class as apiIgnoredCustomerFilter: tsoa drops undeclared query
 *  params, so an omg-api deployed before OMG-2558 answers `?noticeType=…` with
 *  every user, and a "list of users with an arbitration opt-out" would quietly
 *  be the whole user base. An empty result proves nothing, so it is false. */
export const apiIgnoredNoticeFilter = (
  users: readonly Pick<AdminUserListItem, "notices">[],
  filter: NoticeFilter | undefined,
): boolean => {
  if (!filter) return false;
  return users.some(
    (user) => !user.notices || !matchesFilter(user.notices, filter),
  );
};
